import { useState } from 'react'
import './style.css'
import UserBookModal from '../../UserBookModal'
import { bookRatios, BookRatio } from '../bookRatio'

// Reference `v-kval` directive (gtype "match" sizes): >= 1000 -> one-decimal K.
const kval = (value) => {
  const n = Number(value)
  if (n >= 1e3) return `${parseFloat((n / 1e3).toFixed(1))}K`
  return parseFloat(value)
}

// Reference `v-kvalsportmax` directive (gtype "match1" sizes, Max): 1e3..1e5 -> K, >= 1e5 -> L.
const kvalsportmax = (value) => {
  const n = Number(Number(value).toFixed(2))
  if (n >= 1e3 && n < 1e5) return `${parseFloat((n / 1e3).toFixed(2))}K`
  if (n >= 1e5) return `${parseFloat((n / 1e5).toFixed(2))}L`
  return parseFloat(n)
}

const sortBySno = (list) =>
  (list || [])
    .map((s, i) => [s, i])
    .sort((a, b) => (Number(a[0].sno) || 0) - (Number(b[0].sno) || 0) || a[1] - b[1])
    .map(([s]) => s)

const oddsOf = (section, side) =>
  (section?.odds || []).filter((o) => String(o.otype || '').toLowerCase() === side)

/**
 * Reference `Market2` (feed dtype 2) — `.market-2` block: one Back + one Lay column
 * (Tied Match, dtype-2 Bookmakers, Draw No Bet, ...). Markup copies the reference template.
 */
function Market2({ market, index = 0, onBetLockClick, exposureData = {}, eventId }) {
  const [showUserBook, setShowUserBook] = useState(false)
  // Reference getClass(): a box flashes bg-success / bg-danger when its odds moved up / down
  // since the previous feed (marketDataOld). Keep the previous raw market as derived state.
  const [feed, setFeed] = useState({ cur: market, old: null })
  if (market !== feed.cur) setFeed({ cur: market, old: feed.cur })

  if (!market) return null
  const mname = market.mname || market.marketName || market.name || ''
  const gtype = market.gtype
  const sections = sortBySno(market.section)
  const oldSections = sortBySno(feed.old?.section)
  const marketOn = Number(market.gscode) === 1
  const maxBet = market.iplay ? market.maxb : market.umaxbof
  const sizeText = (size) => (gtype === 'match1' ? kvalsportmax(size) : kval(size))
  const bookOf = (nat) => exposureData[nat] ?? exposureData[String(nat || '').trim()]

  const boxClass = (side, o, i) => {
    const old = oddsOf(oldSections[i], side).find((x) => x.tno === o.tno)
    const prev = old ? old.odds : o.odds
    const flash = prev < o.odds ? ' bg-success' : prev > o.odds ? ' bg-danger' : ''
    const tier = (side === 'back' ? ['back', 'back1', 'back2'] : ['lay', 'lay1', 'lay2'])[o.tno]
    return `bl-box ${side}${tier ? ` ${tier}` : ''}${Number(o.odds) === 0 ? ' no-val' : ''}${flash}`
  }

  const renderBox = (side, o, i) => (
    <div key={`${side}odds${o.tno}`} className={boxClass(side, o, i)}>
      <span className="d-block odds">{Number(o.odds) !== 0 ? o.odds : '—'}</span>{' '}
      {Number(o.size) !== 0 && <span className="d-block">{sizeText(o.size)}</span>}
    </div>
  )

  const maxBetEl = gtype === 'match1' ? (
    <span className="max-bet" />
  ) : gtype === 'match' ? (
    <span className="max-bet">
      <span title={`Max : ${kvalsportmax(maxBet)}`}>{kvalsportmax(maxBet)}</span>
    </span>
  ) : (
    <span className="max-bet">{'\n                        Max:'}<span>{kvalsportmax(maxBet)}</span></span>
  )

  return (
    <div className="market-2">
      <div className="bet-table">
        <div className="bet-table-header">
          <div className="nation-name" data-toggle="collapse" data-target={`#market${index}`} aria-expanded="true">
            <span title={mname}>{`\n                    ${mname}\n                `}</span>
          </div>{' '}
          <div className="float-right">
            <a
              href="javascript:void(0)"
              className="btn btn-back"
              onClick={(e) => {
                e.preventDefault()
                if (onBetLockClick) onBetLockClick(mname)
              }}
            >
              Bet Lock
            </a>{' '}
            {(gtype === 'match' || gtype === 'match1') && (
              <a
                href="javascript:void(0)"
                className="btn btn-back"
                onClick={(e) => {
                  e.preventDefault()
                  setShowUserBook(true)
                }}
              >
                User Book
              </a>
            )}
          </div>
        </div>{' '}
        <div
          id={`market${index}`}
          data-title={market.status}
          className={`bet-table-body collapse show${marketOn ? '' : ' suspendedfull'}`}
        >
          <div className="bet-table-row">
            <div className="text-right nation-name">{maxBetEl}</div>{' '}
            <div className="back bl-title d-none-mobile">Back</div>{' '}
            <div className="lay bl-title d-none-mobile">Lay</div>
          </div>{' '}
          {sections.map((section, i) => {
            const book = bookOf(section.nat)
            const ratio = bookRatios(sections.map((s) => bookOf(s.nat)))[i]
            const backs = oddsOf(section, 'back').sort((a, b) => b.tno - a.tno)
            const lays = oddsOf(section, 'lay').sort((a, b) => a.tno - b.tno)
            const suspendedText = marketOn && Number(section.gscode) !== 1
            return (
              <div key={`web${i}`} data-title={section.gstatus} className={`bet-table-row${suspendedText ? ' suspendedtext' : ''}`}>
                <div className="nation-name d-none-mobile">
                  <p>{section.nat}</p>{' '}
                  <p className="mb-0 float-left" style={{ color: Number(book) > 0 ? 'green' : 'red' }}>
                    {book || 0}
                    <BookRatio value={ratio} />
                  </p>{' '}
                  <p className="mb-0 float-right d-none">0</p>
                </div>{' '}
                {backs.map((o) => renderBox('back', o, i))}{' '}
                {lays.map((o) => renderBox('lay', o, i))}
              </div>
            )
          })}
        </div>
      </div>{' '}
      {market.rem ? <div className="remark">{market.rem}</div> : null}
      <UserBookModal
        show={showUserBook}
        onHide={() => setShowUserBook(false)}
        matchId={eventId}
        marketName={mname}
        initialRunners={sections.map((s) => s.nat).filter(Boolean)}
      />
    </div>
  )
}

export default Market2
